import React from "react";
import { UAParser } from "ua-parser-js";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { BreakPoint } from "../models/screen";

export const getUA = (): UAParser.IResult => {
    const parser = new UAParser();
    return parser.getResult();
};

export const isMobile = {
    Android: (): boolean => {
        const ua = getUA();
        return ua.os.name === "Android";
    },
    iOS: (): boolean => {
        const ua = getUA();
        return ua.os.name === "iOS";
    },
    any: (): boolean => {
        const ua = getUA();
        return ua.device.type === "mobile" || ua.device.type === "tablet";
    },
};

export const getViewportBreakpoint = (): BreakPoint => {
    const window = getWindow();
    const width = window ? window.innerWidth : 0;
    if (width > BreakPoint.LARGE) {
        return BreakPoint.LARGE;
    }
    if (width > BreakPoint.MEDIUM) {
        return BreakPoint.MEDIUM;
    }
    if (width > BreakPoint.SMALL) {
        return BreakPoint.SMALL;
    }
    return BreakPoint.MIN;
};

/**
 * SSR-safe hook for detecting mobile/tablet viewports.
 *
 * Defaults to `true` (mobile) to match SSR output — the server has no
 * `window`, so `getViewportBreakpoint()` returns 0, which is below the
 * LARGE breakpoint. After hydration, `useEffect` corrects to the actual
 * viewport width.
 */
export const useIsMobileBreakpoint = (): boolean => {
    const [isMobile, setIsMobile] = React.useState(true);
    React.useEffect(() => {
        const window = getWindow();
        const update = () =>
            setIsMobile(getViewportBreakpoint() < BreakPoint.LARGE);
        update();
        if (window) {
            window.addEventListener("resize", update);
            return () => window.removeEventListener("resize", update);
        }
    }, []);
    return isMobile;
};
